<template>
	<header v-if="!isEmbedded">
		<div class="sub-header">
			<div class="container">
				<a
					href="https://realschools.com.au"
					title="Back to realschools.com.au main site"
				>
					<img
						src="@/images/logo-stacked-reverse.svg?inline"
						alt="Real Schools"
					/>
				</a>
			</div>
		</div>
		<div
			class="main-header"
			:class="{
				'nav-open': menuOpen,
			}"
		>
			<div class="main-header-container container">
				<router-link
					to="/"
					title="Real Schools Partner Portal"
					class="logo-link"
				>
					<h1 class="text-hide">Real Schools Partner Portal</h1>
					<img
						src="@/images/partner-portal.svg?inline"
						alt="Partner Portal"
					/>
				</router-link>

				<template v-if="!$route.meta.hideNav || user.isAdmin">
					<nav v-if="!user.id">
						<ul class="no-list">
							<li>
								<router-link
									:to="{
										name: 'login',
									}"
								>
									Login
								</router-link>
							</li>
						</ul>
					</nav>
					<nav v-else>
						<ul class="no-list">
							<template v-if="user.id">
								<!-- No submenu again. It came back when the Policy Advisor gave
									 Admin a second child; the advisor has since moved to Ops, so the
									 only child left is a link to Admin itself. A menu of one is a
									 click in the way of the page. -->
								<template v-if="user.isAdmin">
									<li>
										<router-link
											:to="{
												name: 'admin',
											}"
										>
											Admin
										</router-link>
									</li>
								</template>

								<template v-if="user.canAccessOps">
									<li>
										<a
											href="#"
											@click.prevent=""
										>
											Ops
										</a>
										<ul>
											<li>
												<router-link
													:to="{
														name: 'activity',
													}"
												>
													Activity
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'event-admin',
													}"
												>
													Events
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'email-library',
													}"
												>
													Emails
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'broadcast-list',
													}"
												>
													Send an email
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'policy-advisor',
													}"
												>
													Policy Advisor
												</router-link>
											</li>
											<!--
												Only for an actual EF. Admins and
												Operations are `isAdminEf` too and
												already have this under Ops, beside the
												advisor it belongs with — without this
												they would see it listed twice.
											-->
											<li v-if="user.isEf">
												<router-link
													:to="{
														name: 'school-policies',
													}"
												>
													School Policies
												</router-link>
											</li>
										</ul>
									</li>
								</template>

								<template v-if="user.isAdminEf">
									<li>
										<a
											href="#"
											@click.prevent=""
										>
											Expert Facilitator
										</a>
										<ul>
											<li>
												<router-link
													:to="{
														name: 'ef-resources',
													}"
												>
													EF Resources
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'broadcast-list',
													}"
												>
													Send an email
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'survey-creator',
													}"
												>
													Survey Creator
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'survey-analyser',
													}"
												>
													Survey Analyser
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'event-bookings',
													}"
												>
													Event Bookings
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'vpa-bookings',
													}"
												>
													VPA Bookings
												</router-link>
											</li>
											<li v-if="user.canChooseSchool">
												<router-link
													:to="{
														name: 'schoolList',
													}"
												>
													Schools
												</router-link>
											</li>
											<!--
												Only for an actual EF. Admins and
												Operations are `isAdminEf` too and
												already have this under Ops, beside the
												advisor it belongs with — without this
												they would see it listed twice.
											-->
											<li v-if="user.isEf">
												<router-link
													:to="{
														name: 'school-policies',
													}"
												>
													School Policies
												</router-link>
											</li>
										</ul>
									</li>
								</template>

								<li
									v-if="user.isAdminEf"
									class="nav-spacer"
								>
									|
								</li>

								<li v-if="user.canAccessOnboarding">
									<router-link
										:to="{
											name: 'onboarding',
										}"
									>
										Onboarding
									</router-link>
								</li>
								<!--
									Partnership is important enough to be surfaced out
									here as well as in the staffroom, but only for the
									champions whose job it is. An EF or admin gets it in
									the staffroom alone.
								-->
								<li v-if="user.canAccessPartnership && user.school?.flags?.partnership_enabled && !user.isAdminEf">
									<router-link
										:to="{
											name: 'partnership',
										}"
										title="Partnership"
									>
										Partnership
									</router-link>
								</li>
								<li v-if="user.canAccessDashboard">
									<router-link
										:to="{
											name: 'dashboard',
										}"
										title="Dashboard"
									>
										Dashboard
									</router-link>
								</li>

								<li v-if="user.canAccessLearning">
									<router-link
										:to="{
											name: 'learning',
										}"
									>
										Learning
									</router-link>
								</li>

								<li v-if="user.canAccessStaffroom">
									<a
										href="#"
										:class="{
											'router-link-active': isInStaffroom,
										}"
									>
										Staffroom
									</a>
									<ul>
										<!--
											Pinned above the registration pages rather than sorted in
											with them by date, and the only one of these that is in the
											nav rather than sent as a link: it is about 2027 for every
											partner, so it is the one page here that suits whoever
											happens to be looking. A year 3 school is linked on to
											their own session from inside it, which is why the year 3
											page is not in this list as well.
										-->
										<li>
											<router-link
												:to="{
													name: 'whats-new-in-partnership',
												}"
											>
												What's New in Partnership
											</router-link>
										</li>
										<!-- The pages currently taking registrations, promoted above the index while they're open, soonest first -->
										<li v-if="user.canAccessRcp">
											<router-link
												:to="{
													name: 'restorative-classroom-playbook',
												}"
											>
												Restorative Classroom Playbook
											</router-link>
										</li>
										<li>
											<router-link
												:to="{
													name: 'live-masterclasses',
												}"
											>
												Live Masterclasses
											</router-link>
										</li>
										<template v-if="user.canAccessStaffroomChampion">
											<li class="group-label">Champion Only ↓</li>
											<li v-if="user.canAccessVpa">
												<router-link
													:to="{
														name: 'virtual-partnership-accelerator',
													}"
												>
													Virtual Partnership Accelerator
												</router-link>
											</li>
											<!--
												`canAccessPartnership` is true
												unconditionally for an EF or admin, so the
												flag has to be checked here too: without
												it this entry and EF Only's both showed
												for them on a school with partnership off.
												Enabled lives here, disabled lives under
												EF Only, and never both.
											-->
											<li v-if="user.canAccessPartnership && user.school?.flags?.partnership_enabled">
												<router-link
													:to="{
														name: 'partnership',
													}"
													title="Partnership"
												>
													Partnership
												</router-link>
											</li>
											<li v-if="user.canAccessDashboard">
												<router-link
													:to="{
														name: 'dashboard',
													}"
													title="Dashboard"
												>
													Dashboard
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'monthly-message',
													}"
												>
													Monthly Message
												</router-link>
											</li>
											<li>
												<router-link
													:to="{
														name: 'staff-management',
													}"
												>
													Staff Management
												</router-link>
											</li>
											<li v-if="user.canAccessDashboard">
												<router-link
													:to="{
														name: 'crisis',
													}"
												>
													Crisis
												</router-link>
											</li>
										</template>

										<!--
											Named for its audience rather than its
											contents, because the audience is the thing
											nobody could see: a champion and an EF are
											shown the same staffroom with different
											items in it, and nothing on screen said
											which were which.

											"EF only" is the label; the gate is EF or
											more, so Operations and administrators see
											it too. They are not EFs, but they are not
											who the label is warning — a champion is.
										-->
										<template v-if="user.isAdminEf">
											<li class="group-label">EF Only ↓</li>
											<li>
												<router-link
													:to="{
														name: 'schoolDetails',
													}"
													title="School Details"
												>
													School Details
												</router-link>
											</li>
											<!--
												Partnership lives here only while it is
												switched off for the school. Once it is
												on it moves up this same list, above
												Dashboard, where champions see it too.
											-->
											<li v-if="user.school && !user.school.flags?.partnership_enabled">
												<router-link
													:to="{
														name: 'partnership',
													}"
													title="Partnership"
												>
													Partnership
												</router-link>
											</li>
										</template>
									</ul>
								</li>

								<li v-if="user.canAccessResources">
									<router-link
										:to="{
											name: 'resourceList',
										}"
									>
										Resources
									</router-link>
								</li>
							</template>

							<li>
								<a
									href="#"
									@click.prevent=""
									title="Account"
									class="my-account-menu"
								>
									<img
										src="@/images/user-circle.svg?inline"
										alt="My account menu"
									/>
								</a>
								<ul>
									<li>
										<router-link
											:v-data-user-id="user.id"
											:to="{
												name: 'account',
											}"
											:title="`You're logged in as ${user.username} (${user.email}) for ${user.school ? user.school.post_title : 'no school'}.`"
										>
											Account
										</router-link>
									</li>
									<li>
										<router-link
											:to="{
												name: 'feedback',
											}"
										>
											Feedback
										</router-link>
									</li>
									<li>
										<a
											:title="logoutTitle"
											@click="logout()"
										>
											Logout
										</a>
									</li>
								</ul>
							</li>
						</ul>
					</nav>

					<button
						class="burger no-style"
						@click="menuOpen = !menuOpen"
					>
						<div />
						<div />
						<div />
					</button>
				</template>
			</div>
		</div>
	</header>
	<main class="rs-app container">
		<router-view />
	</main>
	<footer v-if="!isEmbedded">
		<div class="container footer-inner">All content © Real Schools</div>
	</footer>

	<Transition>
		<button
			v-if="!isEmbedded"
			v-show="shouldShowScrollToTop"
			class="scroll-to-top"
			@click="scrollToTop"
		/>
	</Transition>

	<AddToHomeScreenComponent />

	<SpeedInsights :debug="false" />

	<Analytics :debug="false" />
</template>

<script lang="ts">
import { defineComponent } from 'vue';
import { mapActions, mapStores, setMapStoreSuffix } from 'pinia';
import { useResourceStore } from '@/stores/resourceStore';
import { useDashboardStore } from '@/stores/dashboardStore';
import { isStandalone, isEmbedded, htmlDecode } from '@/helpers';
import AddToHomeScreenComponent from '@/components/AddToHomeScreenComponent.vue';
import { useUser } from '@/stores/userStore';
import type { SchoolExtended } from '@/interfaces/schoolInterface';
import { hubspot } from '@/services/hubspotService';
import { set } from 'vue-gtag';
import { SpeedInsights } from '@vercel/speed-insights/vue';
import { Analytics } from '@vercel/analytics/vue';
import { setUser, setTags } from '@sentry/vue';

setMapStoreSuffix('');

let containerResizeObserver: ResizeObserver | null = null;
let containerResizeRafId: number | null = null;
let lastPostedEmbeddedHeight: number | null = null;

export default defineComponent({
	components: { AddToHomeScreenComponent, SpeedInsights, Analytics },
	data() {
		return {
			menuOpen: false,
			scrollPx: 0,
			isEmbedded,
		};
	},
	computed: {
		shouldShowScrollToTop() {
			return this.scrollPx > 100;
		},
		logoutTitle() {
			return `Logout. Logged in as ${this.user.username}` + (this.user.school ? ` at ${this.user.school.post_title}` : '');
		},
		isInStaffroom() {
			return this.$route.matched.some((route) => {
				return route.name === 'staffroom';
			});
		},
		...mapStores(useUser),
	},
	watch: {
		$route() {
			this.menuOpen = false;
		},
		'user.id': {
			handler(newUserId: number) {
				// loadResources invalidates its own cache when the school changes,
				// so persisted resources survive a refresh and only refetch when
				// actually needed.
				this.loadResources();

				set({
					user_properties: {
						user_id: newUserId ?? 0,
						user_roles: this.user.role,
					},
				});

				if (newUserId) {
					setUser({
						id: newUserId,
						username: this.user.username ?? '',
						email: this.user.email ?? '',
					});
				} else {
					setUser(null);
				}
			},
			immediate: true,
		},
		'user.school.ID': {
			handler(newSchool: SchoolExtended | null) {
				// loadResources detects the school change and invalidates the
				// school-specific resource cache on its own.
				this.loadResources();

				set({
					user_properties: {
						school_id: newSchool ? newSchool.ID : 0,
						school_name: newSchool ? newSchool.post_title : '',
						school_state: newSchool ? newSchool.state : '',
						school_type: newSchool ? newSchool.type : '',
						school_sector: newSchool ? newSchool.sector : '',
						school_year_range: newSchool ? newSchool.yearRange : '',
					},
				});

				setTags({
					school_id: newSchool?.ID,
					school_name: newSchool?.post_title,
				});

				if (newSchool === null) {
					this.clearStore();
				} else {
					if (this.user.canAccessDashboard) {
						this.initDashboardStore();
					}
				}
			},
			immediate: true,
		},
	},
	created() {
		window.addEventListener('scroll', this.handleScroll);
		document.body.classList.add(`display-mode-${isStandalone() ? 'standalone' : 'not-standalone'}`);
	},
	async beforeMount() {
		this.setSchoolIdFromUrl();
		hubspot.init();

		document.addEventListener('visibilitychange', this.handleVisabilityChange);

		if (this.isEmbedded) {
			document.body.classList.add('rs-embedding');
		}

		// Always load resources and user data as the app is initialising
		this.loadResources();
		this.reloadData();
	},
	mounted() {
		if (!this.isEmbedded) {
			return;
		}

		containerResizeObserver = new ResizeObserver((entries) => {
			let latestHeight = lastPostedEmbeddedHeight;

			for (const entry of entries) {
				const cr = entry.contentRect;
				latestHeight = Math.round(cr.height);
			}

			if (latestHeight === null || latestHeight === lastPostedEmbeddedHeight) {
				return;
			}

			if (containerResizeRafId !== null) {
				cancelAnimationFrame(containerResizeRafId);
			}

			containerResizeRafId = requestAnimationFrame(() => {
				if (latestHeight === null || latestHeight === lastPostedEmbeddedHeight) {
					return;
				}

				window.parent.postMessage(
					{
						type: 'resize',
						height: latestHeight,
					},
					'*'
				);

				lastPostedEmbeddedHeight = latestHeight;
				containerResizeRafId = null;
			});
		});
		containerResizeObserver.observe(document.documentElement);
	},
	beforeUnmount() {
		if (containerResizeRafId !== null) {
			cancelAnimationFrame(containerResizeRafId);
			containerResizeRafId = null;
		}

		if (containerResizeObserver) {
			containerResizeObserver.disconnect();
			containerResizeObserver = null;
		}

		lastPostedEmbeddedHeight = null;
	},
	unmounted() {
		window.removeEventListener('scroll', this.handleScroll);
		document.removeEventListener('visibilitychange', this.handleVisabilityChange);
		document.body.classList.remove(
			...[
				'display-mode-standalone',
				'display-mode-not-standalone',
				'rs-embedding',
			]
		);
	},
	methods: {
		htmlDecode,
		scrollToTop() {
			window.scrollTo({ top: 0, left: 0, behavior: 'smooth' });
		},
		handleScroll() {
			this.scrollPx = window.scrollY;
		},
		handleVisabilityChange() {
			const visibility = document.visibilityState;

			// We do this so that stale data is not kept, which would most likely happen if the app is saved to an iOS
			// home screen, and then the app is opened again after a long period of time. Resources is fine to do this with
			// as we handle caching, but the user is loaded every time. For regular useres its probaly not a big deal, but
			// for admins or users with a lot of schools it reloads school data every time. Might be worth handling caching
			// here, too.
			if (visibility === 'visible') {
				this.loadResources();
				this.reloadData();
			}
		},
		...mapActions(useResourceStore, ['loadResources']),
		...mapActions(useUser, ['logout', 'setSchoolIdFromUrl', 'reloadData']),
		...mapActions(useDashboardStore, ['initDashboardStore', 'clearStore']),
	},
});
</script>

<style scoped lang="scss">
header {
	.sub-header {
		display: flex;
		align-items: center;
		background-color: rgb(var(--rs-color-brand-navy));
		min-height: 42px;

		img {
			width: 72px;
		}
	}

	.main-header {
		.main-header-container {
			display: flex;
			position: relative;
			align-items: center;
			min-height: 76px;

			.logo-link {
				img {
					margin-left: -3px;
				}
			}

			nav {
				ul {
					margin-bottom: 0;
					list-style: none;

					li {
						position: relative;
						margin-top: 5px;

						a {
							text-decoration: none;

							&.router-link-exact-active {
								font-weight: bold;
							}
						}

						// A heading inside the menu, marking who the items below it
						// are for. Champion Only was the first; EF Only follows it.
						&.group-label {
							margin-top: 10px;
							border-top: 2px solid rgb(var(--rs-color-brand-navy));
							padding-top: 2px;
							font-weight: bold;
							font-size: 1.2rem;
							text-transform: uppercase;
						}

						.my-account-menu {
							img {
								display: block;
								width: 25px;
							}
						}
					}
				}

				// Only one level of dropdowns, but sometimes we use deeper nested links
				> ul > li > a {
					&.router-link-active {
						font-weight: bold;
					}
				}
			}
		}

		.burger {
			display: flex;
			flex-direction: column;
			justify-content: space-between;
			margin-left: auto;
			padding: var(--rs-layout-content-padding);
			width: 68px;
			height: 64px;

			div {
				transition: all 0.3s;
				background-color: black;
				width: 100%;
				height: 2px;
			}
		}

		&.nav-open {
			nav {
				display: block;
			}

			.burger {
				div {
					&:first-child {
						transform: translateY(11px) rotate(45deg);
					}

					&:nth-child(2) {
						opacity: 0;
					}

					&:last-child {
						transform: translateY(-11px) rotate(-45deg);
					}
				}
			}
		}
	}

	@media screen and (width < 640px) {
		.main-header {
			.main-header-container {
				padding-right: 0;
			}

			nav {
				display: none;
				position: absolute;
				right: 0;
				bottom: 0;
				left: 0;
				transform: translateY(100%);
				z-index: 999999;
				box-shadow: 0 2px 2px rgb(0 0 0 / 20%);
				background-color: white;
				padding: 0 var(--rs-layout-content-padding) var(--rs-layout-content-padding) var(--rs-layout-content-padding);

				li {
					&.nav-spacer {
						display: none;
					}
				}
			}
		}
	}

	@media screen and (width >= 640px) {
		.main-header {
			justify-content: space-between;

			nav {
				margin-left: auto;

				ul {
					display: flex;
					gap: 20px;

					li {
						ul {
							position: absolute;
							top: 100%;
							right: 0;
							flex-direction: column;
							gap: 0;
							z-index: 999999;
							margin: 0;
							box-shadow: 1px 1px 9px 3px #00000047;
							border-radius: var(--rs-border-radius);
							background-color: white;
							padding: 10px 20px 20px 20px;
							text-align: right;

							li {
								a {
									white-space: nowrap;
								}
							}
						}

						a {
							&:hover {
								text-decoration: underline;
							}
						}

						&:not(:hover) {
							ul {
								display: none;
							}
						}
					}
				}
			}

			.burger {
				display: none;
			}
		}
	}
}

main {
	display: flex;
	flex: 1;
	flex-direction: column;
}

footer {
	margin-top: auto;
	background-color: rgb(var(--rs-color-brand-navy));

	.footer-inner {
		display: flex;
		align-items: center;
		min-height: 40px;
		color: rgb(var(--rs-color-brand-white));
		font-size: 14px;
	}

	@media screen and (width >= 640px) {
		.footer-inner {
			min-height: 60px;
		}
	}
}

.scroll-to-top {
	position: fixed;
	right: 40px;
	bottom: 40px;
	transition: all 0.3s ease;
	border: 1px solid rgb(var(--rs-color-brand-navy));
	border-radius: var(--rs-border-radius);
	background-image: url('@/images/carat-up.png');
	background-position: center;
	background-size: 30px;
	background-repeat: no-repeat;
	background-color: rgb(var(--rs-color-brand-sky) / 75%);
	width: 40px;
	height: 40px;

	&:hover {
		background-color: rgb(var(--rs-color-brand-sky) / 50%);
	}

	&.v-enter-from,
	&.v-leave-to {
		opacity: 0;
	}
}
</style>
